import dayjs from "dayjs";
import { useQuery } from "react-query";
import { supabase } from "../../../config/supabaseClient";
import { useCallback, useMemo } from "react";

export function useLoadTypeContentData(
  username: any,
  user_id: any,
  platform: any,
  type: any,
  removedPostIds: any,
  order: string
) {
  // Memoize the removedPostIds processing
  const processedRemovedPostIds = useMemo(() => {
    if (removedPostIds && removedPostIds !== "") {
      return Array.from(
        new Set(removedPostIds?.split(",").filter((elem: any) => elem !== ""))
      );
    }
    return removedPostIds;
  }, [removedPostIds]);

  // Memoize the getPaginatedFeed function
  const getPaginatedFeed = useCallback(async () => {
    try {
      // Validate required parameters
      if (!username || !user_id) {
        throw new Error("Missing required parameters: username or user_id");
      }

      let data = null,
        error = null;

      // Handle different content types
      if (type === "TIKTOK") {
        const { data: content, error: contentError } = await supabase.rpc(
          "get_tiktoktypecontenttteo",
          {
            userid: user_id,
            // user_name: "content_new",
            user_name: username,
            platform_name: platform,
            feed_type: type,
            order_type: order === "DESC" ? "true" : "false",
          }
        );
        data = content;
        error = contentError;
      } else if (type === "STORY") {
        const today = dayjs().utc().format("YYYY-MM-DD");
        const yesterday = dayjs().utc().subtract(7, "day").format("YYYY-MM-DD");
        const { data: content, error: contentError } = await supabase.rpc(
          "get_storytypecontenttteo",
          {
            userid: user_id,
            // user_name: "content_new",
            user_name: username,
            platform_name: platform,
            feed_type: type,
            start_date: yesterday,
            end_date: today,
            order_type: order === "DESC" ? "true" : "false",
          }
        );
        data = content;
        error = contentError;
      } else if (type === "FEATURE") {
        const { data: content, error: contentError } = await supabase.rpc(
          "get_featuredddataaee",
          {
            userid: user_id,
            filter: [
              "FEED",
              "REELS",
              "STORY",
              "HIGHLIGHTS",
              "PIN",
              "VIDEO",
              "PODCAST",
              "TIKTOK",
              "MAIL",
              "TWEET",
              "PRODUCT",
              "POST",
            ],
            query_str: "",
            user_name: username,
            socials: [
              "instagram",
              "youtube",
              "tiktok",
              "podcast",
              "pinterest",
              "newsletter",
              "twitter",
              "LTK",
              "Amazon",
              "ShopMy",
              "MISC",
              "linkedin",
              "MISC-content",
            ],
          }
        );

        data = content;
        error = contentError;
      } else {
        // Check if platform is provided for other types
        if (!platform && type !== "FEATURE") {
          console.warn(`Platform parameter is required for type: ${type}`);
          return []; // Return empty array instead of throwing error
        }

        if (platform) {
          const { data: content, error: contentError } = await supabase.rpc(
            // "get_typecontentteo",
            "get_contenttypeposts",
            {
              userid: user_id,
              // user_name: "content_new",
              user_name: username,
              platform_name: platform,
              feed_type: type === "LINKEDIN" ? "POST" : type,
              order_type: order === "DESC" ? "true" : "false",
            }
          );
          data = content;
          error = contentError;
        }
      }

      // Handle errors from Supabase
      if (error) {
        console.error("Database error:", error);
        throw new Error(`Database error: ${error.message}`);
      }

      // Process and return the data
      if (data && Array.isArray(data)) {
        let filteredArray = data;

        if (processedRemovedPostIds && processedRemovedPostIds.length > 0) {
          filteredArray = data.filter(
            (obj: any) => !processedRemovedPostIds.includes(obj.post_id)
          );
        }

        return filteredArray;
      } else {
        // Return empty array for no data instead of throwing
        return [];
      }
    } catch (err: any) {
      console.error("Error in getPaginatedFeed:", err);
      throw new Error(err.message || "Failed to fetch posts");
    }
  }, [username, user_id, platform, type, processedRemovedPostIds, order]);

  return useQuery(
    ["typeContentPosts", user_id, type, processedRemovedPostIds, order, platform],
    getPaginatedFeed,
    {
      retry: 1,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    }
  );
}
